import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, SwapHorizOutlined,
    CheckCircleOutline, LockOutlined,
    PersonOutlined, AccountBalanceOutlined,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
};

const BANKS = [
    'Access Bank', 'GTBank', 'First Bank', 'Zenith Bank', 'UBA',
    'Kuda Bank', 'Opay', 'Palmpay', 'Stanbic IBTC', 'Polaris Bank',
];

const RECENTS = [
    { name: 'Emeka Okafor',  bank: 'Zenith Bank', acct: '··· 4521', avatar: 'E' },
    { name: 'Fatima Yusuf',  bank: 'GTBank',       acct: '··· 8832', avatar: 'F' },
    { name: 'David Adewale', bank: 'Access Bank',  acct: '··· 3317', avatar: 'D' },
];

const QUICK_AMOUNTS = [1000, 2000, 5000, 10000, 20000];
const WALLET_BALANCE = 125000;

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

const SendPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [step,           setStep]           = useState<1 | 2 | 3 | 4>(1);
    const [amount,         setAmount]         = useState('');
    const [bank,           setBank]           = useState('');
    const [acctNumber,     setAcctNumber]     = useState('');
    const [recipientName,  setRecipientName]  = useState('');
    const [note,           setNote]           = useState('');
    const [loading,        setLoading]        = useState(false);
    const [selectedRecent, setSelectedRecent] = useState<typeof RECENTS[0] | null>(null);

    const numAmount = parseFloat(amount) || 0;
    const fee       = numAmount >= 5000 ? 10 : 0;
    const total     = numAmount + fee;

    // ── Theme tokens ──
    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg,
        border: surfaceBdr,
        boxShadow: surfaceShd,
        borderRadius: '20px',
        backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const handleNext = () => {
        if (step === 1) {
            if (selectedRecent) {
                setBank(selectedRecent.bank);
                setAcctNumber(selectedRecent.acct);
                setRecipientName(selectedRecent.name);
                setStep(3);
            } else if (bank && acctNumber.length === 10) {
                setRecipientName('Chioma Eze');
                setStep(2);
            }
        } else if (step === 2) setStep(3);
        else if (step === 3 && numAmount > 0) {
            setLoading(true);
            setTimeout(() => { setLoading(false); setStep(4); }, 1800);
        }
    };

    const goBack = () => {
        if (step === 1) navigate('/wallet');
        else setStep(prev => (prev - 1) as any);
    };

    /* ── SUCCESS ── */
    if (step === 4) return (
        <Box sx={{ minHeight: '100vh', background: isDark ? `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)` : B.lavender, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: B.greenDim, border: `2px solid ${B.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                    <CheckCircleOutline sx={{ color: B.green, fontSize: 40 }} />
                </Box>
                <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.9rem', mb: 1 }}>Transfer Sent!</Typography>
                <Typography sx={{ color: textMid, fontSize: '.85rem', mb: .5 }}>{fmt(numAmount)} sent to</Typography>
                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '1rem', mb: .25 }}>{recipientName}</Typography>
                <Typography sx={{ color: textFaint, fontSize: '.78rem', mb: 4 }}>{bank} · {acctNumber}</Typography>
                <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center' }}>
                    <Button onClick={() => { setStep(1); setAmount(''); setBank(''); setAcctNumber(''); setSelectedRecent(null); }}
                            sx={{ border: `1px solid ${dividerC}`, color: textMid, borderRadius: 50, px: 3, py: 1.2, textTransform: 'none', fontWeight: 600, '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .05) } }}>
                        Send Again
                    </Button>
                    <Button onClick={() => navigate('/wallet')} variant="contained"
                            sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: 50, px: 4, py: 1.2, textTransform: 'none', fontWeight: 700, boxShadow: `0 12px 32px ${alpha(B.purple, .4)}` }}>
                        Done
                    </Button>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO HEADER ════════════════════════════════════════ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.purple, .1)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={goBack}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>
                            Step {Math.min(step, 3)} of 3
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Send Money
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', background: alpha(B.green, .15), border: `1px solid ${alpha(B.green, .3)}`, borderRadius: 20, px: 1.5, py: .5 }}>
                        <Typography sx={{ color: B.green, fontSize: '.68rem', fontWeight: 700 }}>Bal: {fmt(WALLET_BALANCE)}</Typography>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* STEP 1: Recipient */}
                {step === 1 && (
                    <>
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2.5 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Recent</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {RECENTS.map((r, i) => (
                                    <Box key={i} onClick={() => setSelectedRecent(selectedRecent?.name === r.name ? null : r)} sx={{
                                        display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: '14px', cursor: 'pointer',
                                        background: selectedRecent?.name === r.name ? alpha(B.purple, .1) : 'transparent',
                                        border: `1px solid ${selectedRecent?.name === r.name ? alpha(B.purple, .35) : dividerC}`,
                                        transition: 'all .18s',
                                    }}>
                                        <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Instrument Serif', serif", color: '#fff', fontSize: '1.1rem', flexShrink: 0 }}>
                                            {r.avatar}
                                        </Box>
                                        <Box flex={1}>
                                            <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.85rem' }}>{r.name}</Typography>
                                            <Typography sx={{ color: textFaint, fontSize: '.7rem' }}>{r.bank} · {r.acct}</Typography>
                                        </Box>
                                        {selectedRecent?.name === r.name && <CheckCircleOutline sx={{ color: B.purple, fontSize: 18 }} />}
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {!selectedRecent && (
                            <Box sx={{ ...glassCard, p: 2.5, mb: 2.5 }}>
                                <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>New Recipient</Typography>
                                <Typography sx={{ color: textFaint, fontSize: '.65rem', mb: 1.25 }}>Select Bank</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 2.5 }}>
                                    {BANKS.map(b => (
                                        <Box key={b} onClick={() => setBank(b)} sx={{
                                            p: 1.25, borderRadius: '12px', cursor: 'pointer', textAlign: 'center',
                                            background: bank === b ? alpha(B.purple, .1) : alpha(isDark ? '#fff' : B.dark, .03),
                                            border: `1px solid ${bank === b ? alpha(B.purple, .4) : dividerC}`,
                                            transition: 'all .18s',
                                        }}>
                                            <Typography sx={{ color: bank === b ? B.purple : textMid, fontSize: '.73rem', fontWeight: 600 }}>{b}</Typography>
                                        </Box>
                                    ))}
                                </Box>

                                {bank && (
                                    <>
                                        <Typography sx={{ color: textFaint, fontSize: '.65rem', mb: 1 }}>Account Number</Typography>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            {Array.from({ length: 10 }, (_, i) => (
                                                <Box key={i} sx={{ flex: 1, height: 38, borderRadius: '8px', background: i < acctNumber.length ? alpha(B.purple, .15) : alpha(isDark ? '#fff' : B.dark, .04), border: `1px solid ${i < acctNumber.length ? alpha(B.purple, .4) : dividerC}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.85rem' }}>{acctNumber[i] || ''}</Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                        <Box component="input" value={acctNumber}
                                             onChange={e => setAcctNumber((e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 10))}
                                             type="tel" maxLength={10} placeholder="tap to type"
                                             sx={{ width: '100%', mt: 1.5, p: 1.25, borderRadius: '12px', background: alpha(isDark ? '#fff' : B.dark, .04), border: `1px solid ${dividerC}`, color: textMid, fontSize: '.8rem', outline: 'none', letterSpacing: '.1em', fontFamily: "'DM Sans', sans-serif" }} />
                                    </>
                                )}
                            </Box>
                        )}
                    </>
                )}

                {/* STEP 2: Confirm recipient */}
                {step === 2 && (
                    <Box sx={{ ...glassCard, p: 3, mb: 2.5 }}>
                        <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2.5, fontWeight: 600 }}>Confirm Recipient</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, p: 2, borderRadius: '16px', background: alpha(B.green, .06), border: `1px solid ${alpha(B.green, .18)}` }}>
                            <Box sx={{ width: 52, height: 52, borderRadius: '50%', background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PersonOutlined sx={{ color: '#fff', fontSize: 24 }} />
                            </Box>
                            <Box>
                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '1.05rem', letterSpacing: '-.01em' }}>{recipientName}</Typography>
                                <Typography sx={{ color: textMid, fontSize: '.75rem' }}>{acctNumber}</Typography>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .5, mt: .5, background: alpha(B.green, .1), borderRadius: 10, px: 1, py: .25 }}>
                                    <AccountBalanceOutlined sx={{ color: B.green, fontSize: 11 }} />
                                    <Typography sx={{ color: B.green, fontSize: '.65rem', fontWeight: 700 }}>{bank}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}

                {/* STEP 3: Amount */}
                {step === 3 && (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, p: 1.5, ...glassCard }}>
                            <Box sx={{ width: 36, height: 36, borderRadius: '50%', background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Instrument Serif', serif", color: '#fff', fontSize: '.95rem', flexShrink: 0 }}>
                                {recipientName[0]}
                            </Box>
                            <Box>
                                <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.83rem' }}>{recipientName}</Typography>
                                <Typography sx={{ color: textFaint, fontSize: '.68rem' }}>{bank}</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ ...glassCard, p: 3, mb: 2.5 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Enter Amount</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography sx={{ color: textFaint, fontFamily: "'Instrument Serif', serif", fontSize: '2rem' }}>₦</Typography>
                                <Box component="input" value={amount}
                                     onChange={e => setAmount((e.target as HTMLInputElement).value.replace(/[^0-9]/g, ''))}
                                     placeholder="0" autoFocus
                                     sx={{ background: 'transparent', border: 'none', outline: 'none', color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: { xs: '2.6rem', sm: '3.2rem' }, fontWeight: 400, letterSpacing: '-.03em', width: '100%', '&::placeholder': { color: textFaint } }} />
                            </Box>
                            <Box sx={{ height: '1px', background: `linear-gradient(90deg, ${B.purple}, ${alpha(isDark ? '#fff' : B.dark, .08)})`, mb: 3 }} />
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2.5 }}>
                                {QUICK_AMOUNTS.map(q => (
                                    <Chip key={q} label={`₦${q.toLocaleString()}`} onClick={() => setAmount(String(q))}
                                          sx={{ background: amount === String(q) ? alpha(B.purple, .15) : alpha(isDark ? '#fff' : B.dark, .05), border: `1px solid ${amount === String(q) ? B.purple : dividerC}`, color: amount === String(q) ? B.purple : textMid, fontWeight: 600, fontSize: '.72rem', cursor: 'pointer', '&:hover': { background: alpha(B.purple, .1) } }} />
                                ))}
                            </Box>
                            <Box component="input" value={note}
                                 onChange={e => setNote((e.target as HTMLInputElement).value)}
                                 placeholder="Add a note (optional)"
                                 sx={{ width: '100%', p: 1.5, borderRadius: '12px', background: alpha(isDark ? '#fff' : B.dark, .04), border: `1px solid ${dividerC}`, color: textMid, fontSize: '.8rem', outline: 'none', fontFamily: "'DM Sans', sans-serif", '&::placeholder': { color: textFaint }, '&:focus': { borderColor: alpha(B.purple, .4) }, transition: 'border-color .2s', boxSizing: 'border-box' }} />
                        </Box>

                        {numAmount > 0 && (
                            <Box sx={{ ...glassCard, p: 2, mb: 2.5 }}>
                                <Box display="flex" justifyContent="space-between" mb={.75}>
                                    <Typography sx={{ color: textMid, fontSize: '.75rem' }}>Transfer amount</Typography>
                                    <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.75rem' }}>{fmt(numAmount)}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography sx={{ color: textMid, fontSize: '.75rem' }}>Processing fee</Typography>
                                    <Typography sx={{ color: fee > 0 ? textMid : B.green, fontWeight: 600, fontSize: '.75rem' }}>{fee > 0 ? fmt(fee) : 'Free'}</Typography>
                                </Box>
                                <Box sx={{ height: '1px', background: dividerC, mb: 1 }} />
                                <Box display="flex" justifyContent="space-between">
                                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.85rem' }}>Total deducted</Typography>
                                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.85rem' }}>{fmt(total)}</Typography>
                                </Box>
                            </Box>
                        )}
                    </>
                )}

                {/* CTA */}
                {(
                    (step === 1 && (selectedRecent || (bank && acctNumber.length === 10))) ||
                    step === 2 ||
                    (step === 3 && numAmount >= 100)
                ) && (
                    <Button fullWidth onClick={handleNext} disabled={loading} sx={{
                        background: loading ? alpha(B.purple, .4) : `linear-gradient(135deg, ${B.purple} 0%, ${B.light} 100%)`,
                        color: '#fff', borderRadius: 50, py: 1.8, textTransform: 'none',
                        fontWeight: 700, fontSize: '.95rem',
                        boxShadow: `0 14px 36px ${alpha(B.purple, .35)}`,
                        transition: 'all .25s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 20px 44px ${alpha(B.purple, .5)}` },
                        '&:disabled': { color: alpha('#fff', .35) },
                    }} startIcon={step !== 3 ? undefined : <SwapHorizOutlined />}>
                        {loading ? 'Sending…' :
                            step === 1 ? 'Next — Confirm Recipient' :
                                step === 2 ? 'Looks Good · Continue' :
                                    `Send ${fmt(total)}`}
                    </Button>
                )}

                <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.6rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.06em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default SendPage;